// Phase 41 Plan 41-07 — TanStack Query wrapper around the SLA Balíček
// current-month consumption endpoint.
//
// Pattern mirrors `useCoverageEntries.ts` (queryKey + enabled-on-id) with
// extra refetch tuning per the surface needs:
//   - staleTime 60s — consumption updates as employees log entries; a one-
//     minute window is acceptable for a hero-card display.
//   - refetchInterval 5min — a PM keeping the page open all afternoon
//     should see the bar move; 5min is the balance between freshness and
//     server load (matches per-card refetch policy in 41-RESEARCH Pitfall 7).
//
// `enabled` is gated TWICE: by `!!projectId` (don't fire on initial render)
// AND by the caller-supplied `enabled` flag, which the hero card sets to
// `false` when `paymentTypeId !== SLA_BALICEK_PAYMENT_TYPE_ID`. That guard
// prevents 404 noise from non-Balíček projects.

import { useQuery } from '@tanstack/react-query';
import {
  fetchProjectPackageStatus,
  type ProjectPackageCurrentMonthConsumption,
} from '../services/api/projectPackageStatus';

export type { ProjectPackageCurrentMonthConsumption } from '../services/api/projectPackageStatus';

// Petra staging UAT 2026-05-18 — Hour Package card lagged after logging
// hours on /time; PM had to refresh to see the new consumed total. Drop
// staleTime to 10s and refetch on mount / window focus so the bar moves
// the moment the user comes back to /projects/:id.
export function useProjectPackageStatus(projectId: number | undefined, enabled = true) {
  return useQuery<{ data: ProjectPackageCurrentMonthConsumption }>({
    queryKey: ['projectPackageStatus', projectId],
    queryFn: () => fetchProjectPackageStatus(projectId!),
    enabled: !!projectId && enabled,
    staleTime: 10_000,
    refetchInterval: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}
